import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { getDotColor } from 'commons/src/commonFunctions';
import { SensorComponent } from 'commons/src/components/sensors/SensorComponent';
import SensorLabel from 'commons/src/components/sensors/SensorLabel';
import SensorValue from 'commons/src/components/sensors/SensorValue';
import { languages, mapMoldLinkToLanguage, sensorGraphHeights, sensorUnits } from 'commons/src/constants';
import UnitAndEdgeValues from 'commons/src/features/devicePage/UnitAndEdgeValues';
import { EnumStatusColors, SensorTypes, ThresholdType } from 'commons/src/models/commonEnums';
import {
    AggregateTypeValues,
    AverageSensorPeriodValues,
    ExtraSeriesValues,
    MinSensorPeriodValues,
    SelectedPeriod,
    SensorData,
    VirtualDeviceData,
} from 'commons/src/models/commonTypeScript';
import { Store } from 'commons/src/reducers';
import { CommonRequestType } from 'commons/src/reducers/requestReducer';
import ThresholdDot, { DotGradientVariant } from '../../../components/thresholds/ThresholdDot';
import { sensorRowHeightLimits, ConsumerDeviceStorageKeys } from '../deviceOverviewConstants';
import { chartOnMouseOver } from './SensorConfig';
import SensorGraph from './SensorGraph';

export type PassedProps = {
    deviceId: string;
    sensor: { type: SensorTypes; unit: string; thresholds: Array<number> };
    isExpanded: boolean;
    isMobile: boolean;
    selectedInterval: SelectedPeriod;
    dateFormat: string;
    displayStatusBar: boolean;
    displayResetZoom: () => void;
    timeZone?: string;
    showThresholdLines?: boolean;
    scaleGraphToThresholds?: boolean;
    /** When true, show threshold legend in the right-side box (toggle in header). Min/Max stay in left card when row is expanded. */
    showDetailsPanel?: boolean;
};

type StateProps = {
    language: string;
    sensorData?: { [serialNumber: string]: SensorData };
    virtualSensorData?: { [serialNumber: string]: SensorData };
    endedSegmentData?: { [serialNumber: string]: SensorData };
    minValues?: MinSensorPeriodValues;
    averageValues?: AverageSensorPeriodValues;
    virtualMinValues?: MinSensorPeriodValues;
    virtualAverageValues?: AverageSensorPeriodValues;
    aggregateTypes?: AggregateTypeValues;
    extraSeries?: ExtraSeriesValues;
    extraVirtualSeries?: ExtraSeriesValues;
    endedMinValues?: MinSensorPeriodValues;
    endedAverageValues?: AverageSensorPeriodValues;
    loadingVirtualSensorData: boolean;
    virtualDeviceData: { [serialNumber: string]: VirtualDeviceData };
};

export type Props = PassedProps & StateProps;

export const SensorTileComponent = (props: Props): React.ReactElement => {
    const {
        sensorData = {},
        virtualSensorData = {},
        endedSegmentData = {},
        sensor,
        isExpanded,
        isMobile,
        selectedInterval,
        minValues = {},
        virtualMinValues = {},
        endedMinValues = {},
        averageValues = {},
        virtualAverageValues = {},
        endedAverageValues = {},
        displayStatusBar,
        dateFormat,
        displayResetZoom,
        language,
        deviceId,
        showThresholdLines = true,
        scaleGraphToThresholds = true,
        showDetailsPanel = false,
        extraSeries = {},
        extraVirtualSeries = {},
        aggregateTypes = {},
        loadingVirtualSensorData,
        virtualDeviceData,
        timeZone,
    } = props;

    const { type, unit } = sensor;
    const storageKey = `${deviceId}_${type}`;
    const defaultRowHeight = isExpanded
        ? sensorRowHeightLimits.defaultExpanded
        : sensorRowHeightLimits.defaultMinimized;
    const getStoredHeight = useCallback((): number | null => {
        try {
            const raw = localStorage.getItem(ConsumerDeviceStorageKeys.sensorRowHeights);
            if (!raw) return null;
            const parsed = JSON.parse(raw) as Record<string, number>;
            const value = parsed[storageKey];
            return typeof value === 'number' && value >= sensorRowHeightLimits.min && value <= sensorRowHeightLimits.max
                ? value
                : null;
        } catch {
            return null;
        }
    }, [storageKey]);
    const [rowHeight, setRowHeight] = useState<number>(() => getStoredHeight() ?? defaultRowHeight);
    const [isDragging, setIsDragging] = useState(false);
    const heightDuringDragRef = useRef(rowHeight);

    useEffect(() => {
        const stored = getStoredHeight();
        if (stored != null) setRowHeight(stored);
        else setRowHeight(defaultRowHeight);
    }, [defaultRowHeight, getStoredHeight]);

    const persistHeight = useCallback(
        (height: number) => {
            try {
                const raw = localStorage.getItem(ConsumerDeviceStorageKeys.sensorRowHeights);
                const parsed = (raw ? JSON.parse(raw) : {}) as Record<string, number>;
                parsed[storageKey] = height;
                localStorage.setItem(ConsumerDeviceStorageKeys.sensorRowHeights, JSON.stringify(parsed));
            } catch {
                // ignore
            }
        },
        [storageKey]
    );

    const onResizeHandleMouseDown = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            const startY = e.clientY;
            const startHeight = rowHeight;
            heightDuringDragRef.current = rowHeight;
            setIsDragging(true);
            const onMove = (moveEvent: MouseEvent): void => {
                const delta = moveEvent.clientY - startY;
                const next = Math.max(
                    sensorRowHeightLimits.min,
                    Math.min(sensorRowHeightLimits.max, startHeight + delta)
                );
                heightDuringDragRef.current = next;
                setRowHeight(next);
            };
            const onUp = (): void => {
                persistHeight(heightDuringDragRef.current);
                setIsDragging(false);
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
            };
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
            document.body.style.cursor = 'ns-resize';
            document.body.style.userSelect = 'none';
        },
        [rowHeight, persistHeight]
    );

    const endedData = endedSegmentData[deviceId];
    const currentData = { ...sensorData[deviceId], ...virtualSensorData[deviceId] };
    const chartData = (endedData || currentData)[type];

    const currentExtraSeries = { ...extraSeries[deviceId], ...extraVirtualSeries[deviceId] };
    const extraSeriesForDevice = currentExtraSeries[type];
    const virtualSensorUnitsForDevice = virtualDeviceData[deviceId]?.sensors;
    const extraSeriesUnitsForSensor = virtualSensorUnitsForDevice
        ? virtualSensorUnitsForDevice.find(sensorInfo => sensorInfo.type === type)
        : null;
    const deviceDataAverageValues = averageValues[deviceId];
    const deviceAverageValues = deviceDataAverageValues
        ? { ...deviceDataAverageValues, ...virtualAverageValues[deviceId] }
        : undefined;
    const endedAverages = endedAverageValues[deviceId];
    const averageSensorValues = (deviceAverageValues || endedAverages)[type];

    const deviceDataMinValues = minValues[deviceId];
    const deviceMinValues = deviceDataMinValues ? { ...deviceDataMinValues, ...virtualMinValues[deviceId] } : undefined;
    const endedMin = endedMinValues[deviceId];
    const minSensorValues = (deviceMinValues || endedMin)[type];
    const { t: txt } = useTranslation();

    const effectiveRowHeight = !isMobile ? rowHeight : undefined;
    const chartHeightFromRow = effectiveRowHeight != null ? effectiveRowHeight - 20 : 0;
    const minChartHeight = sensorGraphHeights.deviceViewMinimized;
    let chartHeight: number;
    if (isMobile) {
        chartHeight = sensorGraphHeights.mobileView;
    } else if (effectiveRowHeight != null) {
        chartHeight = Math.max(chartHeightFromRow, minChartHeight);
    } else {
        chartHeight = isExpanded ? sensorGraphHeights.deviceViewExpanded : sensorGraphHeights.deviceViewMinimized;
    }

    const showDecimalsForUnit = ['pci', 'inhg'];
    let averageValue = selectedInterval && averageSensorValues && averageSensorValues[selectedInterval.name];
    averageValue =
        averageValue &&
        (showDecimalsForUnit.includes(sensor.unit) ? averageValue : parseInt(averageValue.toFixed(0), 10));
    const averageDotColor =
        sensor.thresholds.length > 0
            ? getDotColor(sensor.thresholds, sensor.type, averageValue)
            : EnumStatusColors.grey;

    const moldRiskLink = (): React.ReactElement => {
        const moldLink = mapMoldLinkToLanguage[language];
        return (
            <a href={moldLink} target="_blank" rel="noopener noreferrer" className="sensor-row__current-value__link">
                {txt('WhatIsmold')}
            </a>
        );
    };

    const isMoldRisk = type === SensorTypes.mold;
    const isHourlyRadonStD = type === SensorTypes.hourlyRadonStandardDeviation;
    const sensorHasValue = averageValue !== undefined && averageValue !== null && !Number.isNaN(averageValue);
    const extraSeriesForInterval = (!!extraSeriesForDevice && extraSeriesForDevice[selectedInterval.name]) || {};
    const showExtraSeriesName =
        Object.keys(extraSeriesForInterval).length > 0 && Object.values(extraSeriesForInterval)[0].length > 0;
    /* Min/Max (historical data) shown in left card when row is expanded */
    const showEdgeValues =
        isExpanded &&
        sensorHasValue &&
        sensor.type !== SensorTypes.presence &&
        sensor.type !== SensorTypes.airExchangeRate;
    const customSensorName = (): string | undefined => {
        switch (sensor.type) {
            case SensorTypes.presence:
                return 'SensorName.PresenceAndOccupancy';
            default:
                return undefined;
        }
    };

    const showThresholdInfo = showDetailsPanel && !isMobile && sensor.thresholds.length > 0;
    const unitStr = sensorUnits[unit] ?? unit;
    const INLINE_DOT_GRADIENT_TEMP: DotGradientVariant[] = ['blue', 'red'];
    const INLINE_DOT_GRADIENT_2: DotGradientVariant[] = ['greenToYellow', 'yellowToRed'];
    const INLINE_DOT_GRADIENT_4: DotGradientVariant[] = [
        'redToYellow',
        'yellowToGreen',
        'greenToYellow',
        'yellowToRed',
    ];
    const getInlineDotVariant = (index: number): DotGradientVariant => {
        const n = sensor.thresholds.length;
        let variants: readonly DotGradientVariant[];
        if (type === SensorTypes.temp) {
            variants = INLINE_DOT_GRADIENT_TEMP;
        } else if (n === 4) {
            variants = INLINE_DOT_GRADIENT_4;
        } else {
            variants = INLINE_DOT_GRADIENT_2;
        }
        return variants[index] ?? 'yellow';
    };
    const INLINE_TRANSITION_LABELS_2 = ['CustomThresholds.GoodToFair', 'CustomThresholds.FairToPoor'] as const;
    const INLINE_TRANSITION_LABELS_4 = [
        'CustomThresholds.PoorToFair',
        'CustomThresholds.FairToGood',
        'CustomThresholds.GoodToFair',
        'CustomThresholds.FairToPoor',
    ] as const;
    const getInlineTransitionLabel = (index: number): string => {
        const n = sensor.thresholds.length;
        if (type === SensorTypes.temp && n === 2)
            return index === 0 ? txt('ThresholdTypeTemp.LOW_ALERT') : txt('ThresholdTypeTemp.HIGH_ALERT');
        if (n === 4) return INLINE_TRANSITION_LABELS_4[index] ? txt(INLINE_TRANSITION_LABELS_4[index]) : '';
        return INLINE_TRANSITION_LABELS_2[index] ? txt(INLINE_TRANSITION_LABELS_2[index]) : '';
    };

    return (
        <>
            {isHourlyRadonStD && <div className="single-device__header">BETA features:</div>}
            <div
                className={classNames('sensor-row', {
                    'sensor-row--expanded': isExpanded,
                    'sensor-row--resizable': !isMobile,
                    'sensor-row--with-details-panel': showThresholdInfo,
                    'sensor-row--mobile': isMobile,
                })}
                style={!isMobile ? { height: effectiveRowHeight } : undefined}
            >
                {isMobile ? (
                    <div className="sensor-row__current-value-mobile">
                        <SensorLabel sensorType={type} textUppercase />
                        {sensor.type !== SensorTypes.presence && sensor.type !== SensorTypes.airExchangeRate && (
                            <SensorValue
                                sensorValue={averageValue}
                                sensorUnit={unit}
                                title="Average"
                                dotColor={averageDotColor}
                            />
                        )}
                    </div>
                ) : (
                    <div className="sensor-row__current-value">
                        <SensorComponent
                            dotColor={averageDotColor}
                            value={averageValue}
                            sensorType={type}
                            unit={unit}
                            displayStatusBar={displayStatusBar}
                            customSensorName={customSensorName()}
                        />
                        {isMoldRisk && moldRiskLink()}
                        {showEdgeValues && (
                            <UnitAndEdgeValues
                                chartData={chartData}
                                selectedInterval={selectedInterval}
                                sensor={sensor}
                            />
                        )}
                    </div>
                )}
                <div
                    className={isMobile ? 'sensor-row__historical-mobile' : 'sensor-row__historical'}
                    onMouseMove={(e): void => chartOnMouseOver(e.nativeEvent, false)}
                    onMouseLeave={(e): void => chartOnMouseOver(e.nativeEvent, true)}
                >
                    <SensorGraph
                        key={`sensor-graph-${type}-${showThresholdInfo}`}
                        isResizing={isDragging}
                        showThresholdLines={showThresholdLines}
                        scaleGraphToThresholds={scaleGraphToThresholds}
                        timeZone={timeZone}
                        chartData={chartData}
                        extraSeries={extraSeriesForDevice}
                        aggregateTypes={aggregateTypes[deviceId]}
                        selectedInterval={selectedInterval}
                        thresholds={sensor.thresholds}
                        chartHeight={chartHeight}
                        sensorType={sensor.type}
                        unit={sensor.unit}
                        minValues={minSensorValues}
                        averageValues={averageSensorValues}
                        dateFormat={dateFormat}
                        displayResetZoom={displayResetZoom}
                        loadingVirtualSensor={loadingVirtualSensorData}
                        extraSeriesUnitsForSensor={extraSeriesUnitsForSensor?.series}
                        showExtraSeriesName={showExtraSeriesName}
                    />
                </div>
                {!isMobile && showThresholdInfo && (
                    <div className="sensor-row__side-panel">
                        <div className="sensor-row__side-panel__thresholds">
                            <span className="sensor-row__side-panel__thresholds-label">
                                {txt('CustomThresholds.Thresholds')}:
                            </span>
                            <div className="sensor-row__side-panel__thresholds-row">
                                {sensor.thresholds.map((value, i) => (
                                    <span
                                        key={`${type}-threshold-${value}`}
                                        className="sensor-row__side-panel__thresholds-item"
                                    >
                                        <ThresholdDot
                                            type={i === 0 ? ThresholdType.LOW_ALERT : ThresholdType.HIGH_ALERT}
                                            variant={getInlineDotVariant(i)}
                                        />
                                        <span className="sensor-row__side-panel__thresholds-label-value">
                                            <span className="sensor-row__side-panel__thresholds-transition">
                                                {getInlineTransitionLabel(i)}
                                            </span>
                                            <span className="sensor-row__side-panel__thresholds-value">
                                                {value} {unitStr}
                                            </span>
                                        </span>
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
                {!isMobile && (
                    <div
                        role="button"
                        tabIndex={0}
                        aria-label={txt('Resize')}
                        className="sensor-row__resize-handle"
                        onMouseDown={onResizeHandleMouseDown}
                        onKeyDown={(e): void => {
                            if (e.key === 'Enter' || e.key === ' ') e.preventDefault();
                        }}
                        data-testid="sensor-row-resize-handle"
                    />
                )}
            </div>
        </>
    );
};

const mapStateToProps = (state: Store): StateProps => {
    const {
        userSettings: { language },
        deviceSensorData: {
            sensorData,
            virtualSensorData,
            virtualMinValues,
            virtualAverageValues,
            minValues,
            averageValues,
            extraSeries,
            aggregateTypes,
            extraVirtualSeries,
            virtualDeviceData,
        },
        endedSegmentData: {
            sensorData: endedSegmentData,
            averageValues: endedAverageValues,
            minValues: endedMinValues,
        },
        commonRequests: {
            [CommonRequestType.FetchCustomSegmentVirtualSensors]: { loading: loadingCustomVirtualSensors },
            [CommonRequestType.PollVirtualDeviceSensorData]: { loading: loadingVirtualSensorData },
        },
    } = state;

    return {
        language: Object.prototype.hasOwnProperty.call(languages, language) ? language : 'en',
        sensorData,
        endedSegmentData,
        minValues,
        averageValues,
        endedAverageValues,
        endedMinValues,
        extraSeries,
        aggregateTypes,
        virtualSensorData,
        virtualMinValues,
        virtualAverageValues,
        extraVirtualSeries,
        loadingVirtualSensorData: loadingCustomVirtualSensors || loadingVirtualSensorData,
        virtualDeviceData,
    };
};

export default connect(mapStateToProps)(SensorTileComponent);
